'use client';

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { BaseFilterProps } from '../types';

interface ConnectorSelectProps
  extends Pick<BaseFilterProps, 'value' | 'onChange'> {
  /** @deprecated Title prop is not currently used but kept for API compatibility */
  title?: string;
  isAll: boolean;
}

const ConnectorSelect: React.FC<ConnectorSelectProps> = ({
  value,
  onChange,
  isAll,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = () => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      // Try to find the drawer container
      const drawerContainer =
        containerRef.current.closest('[role="dialog"]') ||
        containerRef.current.closest('.drawer') ||
        containerRef.current.closest('[data-testid*="drawer"]');

      let containerBottom = window.innerHeight;
      if (drawerContainer) {
        const drawerRect = drawerContainer.getBoundingClientRect();
        containerBottom = drawerRect.bottom;
      }

      const spaceBelow = containerBottom - rect.bottom;
      const minSpaceNeeded = 300; // Need 300px to open below (more conservative)

      // Check if should open above
      const shouldOpenAbove = spaceBelow < minSpaceNeeded;
      setOpenAbove(shouldOpenAbove);
    }
  };

  const handleChange = (newValue: string) => {
    onChange({
      ...value,
      is_all: newValue === 'all',
    });
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(updateDropdownPosition, 0);
      
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="inline-flex justify-between items-center gap-1 cursor-pointer w-full"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="filter-select-connector"
      >
        <div className="justify-start text-primary text-base font-normal font-['Inter'] leading-normal">
          {isAll ? 'All' : 'Any'}
        </div>

        <div className="w-4 h-6 flex items-center justify-center">
          <Icon
            icon={faCaretDown as IconProp}
            className="text-neutral-600 w-4 h-4"
          />
        </div>
      </div>

      {isOpen && (
        <div className={`absolute -left-1 bg-white shadow-lg rounded-md border border-neutral-200 z-50 ${
          openAbove ? 'bottom-full mb-3' : 'top-full mt-3'
        }`}>
          <div
            className="px-3 py-2 hover:bg-neutral-100 cursor-pointer"
            onClick={() => handleChange('all')}
          >
            All
          </div>
          <div
            className="px-3 py-2 hover:bg-neutral-100 cursor-pointer"
            onClick={() => handleChange('any')}
          >
            Any
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectorSelect;
